/* eslint-disable react/jsx-no-literals */
/* eslint-disable formatjs/no-literal-string-in-jsx */
/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-shadow */
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { useIntl, FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import Input from '../form/Input';
import BottomBar from '../BottomBar';
import RadioButton from '../form/RadioButton';
import { yesNo } from '../helpers';
import usePersistedDraft from '../hooks/usePersistedDraft';
import {
  updatePersonalDetails,
  updatePreferences,
  updateProviderDetails,
  updatePhoneNumbers,
  updateAddress,
  updatePracticeDetails,
} from '../../../actions/preferences';

const errorStyle = {
  color: 'red',
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

function TaxInformation({
  openLoadingPanel,
  closeLoadingPanel,
  nextPage,
  errorCallback,
  updateProviderDetails,
  skipToEnd,
  specialized,
  draftValues,
  onDraftChange,
  ...bottomBarProps
}) {
  const intl = useIntl();
  const { control, handleSubmit, watch, getValues } = useForm({
    defaultValues: draftValues || {
      custom_tax_yn: '',
      hst_yn: '',
      gst_hst_number: '',
      pst_number: '',
    },
  });

  usePersistedDraft(getValues, onDraftChange);

  const hstYn = watch('hst_yn'); // replaces formValueSelector

  const onSubmit = values => {
    const errorMessage = intl.formatMessage({
      defaultMessage: 'There was a problem updating your tax information',
    });

    const parsedValues = {
      custom_tax_yn: values.custom_tax_yn,
      gst_hst_number: values.gst_hst_number,
      pst_number: values.pst_number,
    };

    openLoadingPanel(
      intl.formatMessage({
        defaultMessage: 'Updating Tax Information',
      }),
    );
    Promise.all([updateProviderDetails(parsedValues)])
      .then(() => {
        closeLoadingPanel();
        nextPage();
      })
      .catch(error => errorCallback(error, errorMessage));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="page-title" style={{ marginBottom: '20px' }}>
        <FormattedMessage tagName="h1" defaultMessage="Tax Information" />
      </div>
      <h4 style={{ marginBottom: '40px' }}>
        <FormattedMessage defaultMessage="We need the following information to make payments to your account. This information is confidential and will be deleted if you choose to deactivate your GreenShield Health account" />
      </h4>
      {/* Custom Tax Exempt */}
      <p className="onboarding-label">
        {intl.formatMessage({
          defaultMessage: 'Are your clinical services tax exempt?',
        })}{' '}
        *
      </p>
      <div className="input-row">
        <Controller
          name="custom_tax_yn"
          control={control}
          rules={{
            validate: value =>
              value && value.length > 0
                ? true
                : intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState }) => (
            <RadioButton
              {...field}
              onChange={value => {
                field.onChange(value);
              }}
              options={yesNo}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
      {/* HST / GST Registered */}
      <p className="onboarding-label">
        {intl.formatMessage({
          defaultMessage:
            'Are you registered to charge taxes for your services?',
        })}{' '}
        *
      </p>
      <p className="onboarding-sub-label">
        <FormattedMessage defaultMessage="We require a registered tax number in order to pay out taxes on applicable clinical services. An HST or GST number is mandatory for all provinces and territories, but a PST, RST, or QST number may be required depending on the province that you practice in. Your HST or GST number is a 15 digit code made up of 9 numbers, followed by 2 letters, followed by 4 more numbers, and should have the following format: 123456789RT0001. Please consult our knowledge base for additional details" />
      </p>
      <div className="input-row">
        <Controller
          name="hst_yn"
          control={control}
          rules={{
            validate: value =>
              value && value.length > 0
                ? true
                : intl.formatMessage({ defaultMessage: 'Required' }),
          }}
          render={({ field, fieldState }) => (
            <RadioButton
              {...field}
              options={yesNo}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
      {/* GST / HST Number */}
      {hstYn === 'true' && (
        <>
          <p className="onboarding-label">
            {intl.formatMessage({
              defaultMessage: 'HST or GST number (required)',
            })}{' '}
            *
          </p>
          <div className="input-row">
            <Controller
              name="gst_hst_number"
              control={control}
              rules={{
                required: intl.formatMessage({ defaultMessage: 'Required' }),
                pattern: {
                  value: /^\d{9}[A-Za-z]{2}\d{4}$/,
                  message: intl.formatMessage({
                    defaultMessage:
                      'Your HST or GST number must have the format 123456789RT0001',
                  }),
                },
              }}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  maxLength={15}
                  error={fieldState.error?.message}
                  errorStyle={errorStyle}
                />
              )}
            />
          </div>

          <p className="onboarding-label">
            {intl.formatMessage({
              defaultMessage: 'PST, RST, or QST number (if applicable)',
            })}
          </p>
          <div className="input-row">
            <Controller
              name="pst_number"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  maxLength={16}
                  error={fieldState.error?.message}
                  errorStyle={errorStyle}
                />
              )}
            />
          </div>
        </>
      )}
      <BottomBar
        {...bottomBarProps}
        skipToEnd={skipToEnd}
        specialized={specialized}
      />
    </form>
  );
}

TaxInformation.propTypes = {
  openLoadingPanel: PropTypes.func.isRequired,
  closeLoadingPanel: PropTypes.func.isRequired,
  nextPage: PropTypes.func.isRequired,
  skipToEnd: PropTypes.func.isRequired,

  errorCallback: PropTypes.func.isRequired,
  updateProviderDetails: PropTypes.func.isRequired,
  draftValues: PropTypes.object,
  onDraftChange: PropTypes.func,
  specialized: PropTypes.bool,
};

export default connect(null, {
  updatePersonalDetails,
  updatePreferences,
  updateProviderDetails,
  updatePhoneNumbers,
  updateAddress,
  updatePracticeDetails,
})(TaxInformation);
